/**
 * Agreement Type Selector Component
 *
 * Multi-select component for selecting agreement types when a score is insufficient (1-2).
 * Used in the evaluation wizard steps (Step2Tasks, Step3Competencies, Step4BasisCompetencies).
 *
 * Now includes text fields for each selected agreement type to describe the specific agreement.
 */

import { useAgreementTypes } from '../../settings/useSettings';

// Agreement with description
export interface AgreementWithDescription {
  agreementTypeId: number;
  beschrijving: string;
}

interface AgreementTypeSelectorProps {
  selectedTypes: number[];
  onChange: (types: number[]) => void;
  isRequired: boolean;
  disabled?: boolean;
  // New: support for agreement descriptions
  agreements?: AgreementWithDescription[];
  onAgreementsChange?: (agreements: AgreementWithDescription[]) => void;
}

export function AgreementTypeSelector({
  selectedTypes,
  onChange,
  isRequired,
  disabled = false,
  agreements = [],
  onAgreementsChange,
}: AgreementTypeSelectorProps) {
  const { data: agreementTypes, isLoading } = useAgreementTypes();

  const handleToggle = (typeId: number) => {
    if (disabled) return;

    if (selectedTypes.includes(typeId)) {
      // Removing type - also remove from agreements
      onChange(selectedTypes.filter((id) => id !== typeId));
      if (onAgreementsChange) {
        onAgreementsChange(agreements.filter((a) => a.agreementTypeId !== typeId));
      }
    } else {
      // Adding type - also add to agreements with empty description
      onChange([...selectedTypes, typeId]);
      if (onAgreementsChange) {
        onAgreementsChange([...agreements, { agreementTypeId: typeId, beschrijving: '' }]);
      }
    }
  };

  const handleDescriptionChange = (typeId: number, beschrijving: string) => {
    if (!onAgreementsChange) return;

    const existingIndex = agreements.findIndex((a) => a.agreementTypeId === typeId);
    if (existingIndex >= 0) {
      const updated = [...agreements];
      updated[existingIndex] = { agreementTypeId: typeId, beschrijving };
      onAgreementsChange(updated);
    } else {
      onAgreementsChange([...agreements, { agreementTypeId: typeId, beschrijving } as AgreementWithDescription]);
    }
  };

  const getDescription = (typeId: number): string => {
    return agreements.find((a) => a.agreementTypeId === typeId)?.beschrijving || '';
  };

  if (isLoading) {
    return (
      <div className="text-sm text-gray-500">Afspraaktypes laden...</div>
    );
  }

  const activeTypes = agreementTypes?.filter((t) => t.isActive) || [];

  if (activeTypes.length === 0) {
    return null;
  }

  // Check if any selected type has empty description (when descriptions are enabled)
  const hasEmptyDescriptions = onAgreementsChange && selectedTypes.some(
    (typeId) => !getDescription(typeId).trim()
  );

  return (
    <div className="mt-3">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Afspraaktype{isRequired && <span className="text-red-500 ml-1">*</span>}
        {isRequired && (
          <span className="ml-2 text-xs font-normal text-orange-600">
            (verplicht bij score 1 of 2)
          </span>
        )}
      </label>
      <div className="flex flex-wrap gap-2">
        {activeTypes.map((type) => {
          const isSelected = selectedTypes.includes(type.id);
          return (
            <button
              key={type.id}
              type="button"
              onClick={() => handleToggle(type.id)}
              disabled={disabled}
              className={`
                px-3 py-1.5 text-sm rounded-lg border-2 transition-all
                ${isSelected
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
              title={type.description || type.displayName}
            >
              {type.icon && <span className="mr-1">{type.icon}</span>}
              {type.displayName}
            </button>
          );
        })}
      </div>

      {/* Text fields for each selected agreement type */}
      {onAgreementsChange && selectedTypes.length > 0 && (
        <div className="mt-3 space-y-3">
          {selectedTypes.map((typeId) => {
            const type = activeTypes.find((t) => t.id === typeId);
            if (!type) return null;
            const description = getDescription(typeId);
            const isEmpty = !description.trim();

            return (
              <div key={typeId} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {type.icon && <span className="mr-1">{type.icon}</span>}
                  {type.displayName} - Afspraak beschrijving
                  {isRequired && <span className="text-red-500 ml-1">*</span>}
                </label>
                <textarea
                  rows={2}
                  value={description}
                  onChange={(e) => handleDescriptionChange(typeId, e.target.value)}
                  disabled={disabled}
                  placeholder={`Beschrijf de afspraak voor ${type.displayName}...`}
                  className={`
                    w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                    ${isEmpty && isRequired ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                    ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}
                  `}
                />
                {isEmpty && isRequired && (
                  <p className="mt-1 text-xs text-red-500">
                    Beschrijving is verplicht
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isRequired && selectedTypes.length === 0 && (
        <p className="mt-1 text-xs text-red-500">
          Selecteer minimaal één afspraaktype
        </p>
      )}
      {isRequired && hasEmptyDescriptions && (
        <p className="mt-1 text-xs text-red-500">
          Vul alle afspraak beschrijvingen in
        </p>
      )}
    </div>
  );
}

/**
 * Helper to determine if agreement types are required based on score
 */
export function isAgreementTypeRequired(score: number | null): boolean {
  return score !== null && score <= 2;
}

/**
 * Read-only display of selected agreement types (for print/review)
 */
interface AgreementTypeDisplayProps {
  selectedTypeIds: number[];
  showCheckboxes?: boolean;
}

export function AgreementTypeDisplay({ selectedTypeIds, showCheckboxes = false }: AgreementTypeDisplayProps) {
  const { data: agreementTypes } = useAgreementTypes();

  if (!agreementTypes || selectedTypeIds.length === 0) {
    return null;
  }

  const selectedTypes = agreementTypes.filter((t) => selectedTypeIds.includes(t.id));

  if (showCheckboxes) {
    // Print view: show all types with checkboxes
    const activeTypes = agreementTypes.filter((t) => t.isActive);
    return (
      <div className="flex flex-wrap gap-3 mt-2">
        {activeTypes.map((type) => {
          const isSelected = selectedTypeIds.includes(type.id);
          return (
            <label key={type.id} className="flex items-center gap-1.5 text-sm">
              <span className={`
                w-4 h-4 border-2 rounded flex items-center justify-center
                ${isSelected ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-400'}
              `}>
                {isSelected && (
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </span>
              <span className={isSelected ? 'text-gray-900' : 'text-gray-500'}>
                {type.displayName}
              </span>
            </label>
          );
        })}
      </div>
    );
  }

  // Standard display: only show selected types
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {selectedTypes.map((type) => (
        <span
          key={type.id}
          className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full"
        >
          {type.icon && <span className="mr-0.5">{type.icon}</span>}
          {type.displayName}
        </span>
      ))}
    </div>
  );
}

/**
 * Print-optimized agreement type checkboxes (for PDF/print)
 */
interface PrintAgreementTypeCheckboxesProps {
  selectedTypeIds: number[];
  agreementTypes: Array<{ id: number; displayName: string; icon?: string | null }>;
}

export function PrintAgreementTypeCheckboxes({
  selectedTypeIds,
  agreementTypes
}: PrintAgreementTypeCheckboxesProps) {
  return (
    <div className="agreement-types-print mt-2">
      <span className="text-sm text-gray-600 mr-2">Afspraak:</span>
      <div className="inline-flex flex-wrap gap-3">
        {agreementTypes.map((type) => {
          const isSelected = selectedTypeIds.includes(type.id);
          return (
            <label key={type.id} className="flex items-center gap-1 text-sm">
              <span className={`
                agreement-checkbox w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0
                ${isSelected ? 'filled border-blue-600 bg-blue-600' : 'border-gray-400'}
              `}>
                {isSelected && (
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </span>
              <span className="text-gray-700">{type.displayName}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
